import { DeviceType } from '@/api/client'
import type { Event } from '@/classes/Event'
import { Task } from '@/classes/Task'
import { useAuthStore } from '@/stores/AuthStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useEmployeeStore } from '@/stores/EmployeeStore'
import { useEventStore } from '@/stores/EventStore'
import { useProjectStore } from '@/stores/ProjectStore'
import { useTaskStore } from '@/stores/TaskStore'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useAppStateStore } from '@/stores/AppStateStore'

import type {
  DeliveredNotificationSchema,
  PendingLocalNotificationSchema,
} from '@capacitor/local-notifications'
import { LocalNotifications } from '@capacitor/local-notifications'

async function checkAndRequest() {
  if ((await LocalNotifications.checkPermissions()).display === 'granted') {
    return 'granted'
  }
  console.debug(
    'Requesting push notification permission in useLocalNotifications',
  )
  const result = await LocalNotifications.requestPermissions()
  if (result.display === 'granted') return result.display
  return LocalNotifications.requestPermissions()
}

async function fetchOngoingNotifications() {
  const [delivered, pending] = await Promise.all([
    LocalNotifications.getDeliveredNotifications(),
    LocalNotifications.getPending(),
  ])

  return {
    delivered: delivered.notifications.filter(
      (notification) => notification.id === 1,
    ),
    pending: pending.notifications.filter(
      (notification) => notification.id === 1,
    ),
  }
}

function composeNotificationBody(
  task: Task | undefined,
  task_id: number,
  employeeCount: number,
) {
  const body = task
    ? task.getSentence(employeeCount)
    : Task.getSentence(task_id, employeeCount)

  if (task_id === Task.id.PROJECT) {
    const projectName = useProjectStore().project?.name
    return projectName ? `${body} - ${projectName}` : body
  }

  return body
}

function createNotificationOptions(
  body: string,
  custom_color?: string | null,
  custom_design_enabled?: boolean,
) {
  return {
    notifications: [
      {
        title: 'clockin',
        body: body,
        id: 1,
        ongoing: true,
        iconColor: custom_design_enabled
          ? (custom_color ?? '#1a9dd9')
          : '#1a9dd9',
        channelId: '1',
      },
    ],
  }
}

async function createOrUpdateOngoingNotification(lastEvent: Event) {
  try {
    if (!lastEvent.employee_id) return

    const { delivered, pending } = await fetchOngoingNotifications()

    const task = await useTaskStore().taskOfEmployeeById(
      lastEvent.employee_id,
      lastEvent.task_id,
    )
    const employeeCount = useEmployeeStore().clockedInEmployees.length
    const body = composeNotificationBody(task, lastEvent.task_id, employeeCount)

    if (
      task &&
      (delivered.some((n: DeliveredNotificationSchema) => n.body === body) ||
        pending.some((n: PendingLocalNotificationSchema) => n.body === body))
    )
      return

    const { custom_color, custom_design_enabled } = useConfigStore()
    const notificationOptions = createNotificationOptions(
      body,
      custom_color,
      custom_design_enabled,
    )

    await LocalNotifications.schedule(notificationOptions)
  } catch (error) {
    console.error('Error in createOrUpdateOngoingNotification:', error)
  }
}

async function createAndroidChannel() {
  if (Capacitor.getPlatform() !== 'android') return

  const currentChannels = await LocalNotifications.listChannels()
  if (currentChannels.channels.some((channel) => channel.id === '1')) return
  return LocalNotifications.createChannel({
    id: '1',
    name: 'clockin',
    description: '',
    importance: 2,
    visibility: 1,
    vibration: false,
  })
}

async function killCurrentOngoingNotifications() {
  try {
    const pending = await LocalNotifications.getPending()
    const pendingToRemove = pending.notifications.filter(
      (notification) => notification.id === 1,
    )

    if (pendingToRemove.length > 0) {
      await LocalNotifications.cancel({ notifications: pendingToRemove })
    }

    const delivered = await LocalNotifications.getDeliveredNotifications()
    const deliveredToRemove = delivered.notifications.filter(
      (notification) => notification.id === 1,
    )

    if (deliveredToRemove.length > 0) {
      await LocalNotifications.removeDeliveredNotifications({
        notifications: deliveredToRemove,
      })
    }
  } catch (error) {
    console.error('Error while cancelling the ongoing notifications:', error)
  }
}

async function registerOngoingNotification() {
  if (useAuthStore().device_type === DeviceType.TERMINAL) return
  if (!useEventStore().activeEmployeeIsClockedIn) return
  if (useAppStateStore().appBackgroundedByPermissionCheck) return
  const result = await checkAndRequest()
  if (result !== 'granted') return

  const lastEvent = useEventStore().lastEvent

  try {
    const { isActive } = await App.getState()

    if (isActive || !lastEvent || lastEvent.task_id === Task.id.CLOCKOUT) {
      void killCurrentOngoingNotifications()
      return
    }

    await createAndroidChannel()
    void createOrUpdateOngoingNotification(lastEvent)
  } catch (e) {
    console.error('Error while registering an ongoing notification:', e)
  }
}

export { registerOngoingNotification, killCurrentOngoingNotifications }
